/**
 * usePricing — central pricing hook.
 *
 * Single source of truth: the backend `/api/billing/plans` endpoint, which is
 * itself derived from the active Stripe Price IDs configured in `.env`. Any
 * marketing or checkout surface that needs to show prices must use this hook
 * instead of hardcoding values — otherwise prices can silently drift away
 * from Stripe.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/auth-context";

const fmtUsd = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount || 0);

export function usePricing() {
  const [plans, setPlans] = useState([]);
  const [meta, setMeta] = useState({ currency: "usd", trial_days: 14, trial_plan: "professional" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/billing/plans")
      .then(({ data }) => {
        if (cancelled) return;
        setPlans(data.plans || []);
        setMeta({
          currency: data.currency || "usd",
          trial_days: data.trial_days,
          trial_plan: data.trial_plan,
        });
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Could not load pricing");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return { plans, meta, loading, error, fmtUsd };
}

export { fmtUsd };
